// Centralized Google Maps loader to prevent multiple script loads and race conditions
import { useEffect, useState } from 'react'

interface GoogleMapsLoaderOptions {
  apiKey: string
  libraries?: string[]
  language?: string
  region?: string
}

class GoogleMapsLoader {
  private static instance: GoogleMapsLoader
  private loadPromise: Promise<void> | null = null
  private isLoaded = false
  private apiKey: string = ''

  private constructor() {}

  static getInstance(): GoogleMapsLoader {
    if (!GoogleMapsLoader.instance) {
      GoogleMapsLoader.instance = new GoogleMapsLoader()
    }
    return GoogleMapsLoader.instance
  }

  setApiKey(apiKey: string) {
    this.apiKey = apiKey
  }

  async load(options?: Partial<GoogleMapsLoaderOptions>): Promise<void> {
    // If already loaded, return immediately
    if (this.isLoaded && window.google?.maps) {
      return Promise.resolve()
    }

    // If currently loading, return the existing promise
    if (this.loadPromise) {
      return this.loadPromise
    }

    // Start loading
    this.loadPromise = this.loadScript(options)
    
    try {
      await this.loadPromise
      this.isLoaded = true
    } catch (error) {
      this.loadPromise = null
      throw error
    }

    return this.loadPromise
  }

  private loadScript(options?: Partial<GoogleMapsLoaderOptions>): Promise<void> {
    return new Promise((resolve, reject) => {
      // Get API key from options or window
      const apiKey = options?.apiKey || this.apiKey || (window as any).GOOGLE_MAPS_API_KEY

      if (!apiKey || apiKey === 'your-google-maps-api-key') {
        reject(new Error('Google Maps API key not configured'))
        return
      }

      // Check if already loaded
      if (window.google?.maps) {
        resolve()
        return
      }

      // Check if script already exists
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]')
      if (existingScript) {
        // Wait for existing script to load. With loading=async the script tag
        // resolves before libraries are ready, so we need to wait for
        // importLibrary, not just google.maps.
        const checkInterval = setInterval(() => {
          if (window.google?.maps?.importLibrary) {
            clearInterval(checkInterval)
            resolve()
          }
        }, 100)

        // Timeout after 10 seconds
        setTimeout(() => {
          clearInterval(checkInterval)
          if (!window.google?.maps?.importLibrary) {
            reject(new Error('Google Maps failed to load'))
          }
        }, 10000)
        return
      }

      // Create and load script
      const script = document.createElement('script')
      const libraries = options?.libraries || ['places']
      const language = options?.language || 'nl'
      const region = options?.region || 'NL'

      // Create unique callback name
      const callbackName = `gmapsCallback_${Date.now()}`

      // loading=async is required for the new modular Places API
      // (AutocompleteSuggestion, Place class). Without it Google falls back
      // to the legacy bootstrap which doesn't expose the new classes.
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async&v=weekly&libraries=${libraries.join(',')}&language=${language}&region=${region}&callback=${callbackName}`
      script.async = true
      script.defer = true

      // Handle load success
      ;(window as any)[callbackName] = () => {
        delete (window as any)[callbackName]
        resolve()
      }

      // Handle load error
      script.onerror = () => {
        delete (window as any)[callbackName]
        this.loadPromise = null
        reject(new Error('Failed to load Google Maps script'))
      }

      document.head.appendChild(script)
    })
  }

  isGoogleMapsLoaded(): boolean {
    return this.isLoaded && !!window.google?.maps
  }

  reset() {
    this.isLoaded = false
    this.loadPromise = null
  }
}

export const googleMapsLoader = GoogleMapsLoader.getInstance()

// Helper hook for React components
export function useGoogleMaps(apiKey?: string) {
  const [isLoaded, setIsLoaded] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (apiKey) {
      googleMapsLoader.setApiKey(apiKey)
    }

    googleMapsLoader
      .load()
      .then(() => {
        setIsLoaded(true)
        setError(null)
      })
      .catch((err) => {
        setError(err instanceof Error ? err : new Error(String(err)))
        setIsLoaded(false)
      })
  }, [apiKey])

  return { isLoaded, error }
}

// For use outside React components
export async function loadGoogleMaps(apiKey?: string): Promise<void> {
  if (apiKey) {
    googleMapsLoader.setApiKey(apiKey)
  }
  return googleMapsLoader.load()
}